import React from "react"
import {
  ActionType,
  BrandLogoBlock,
  NavTab,
  SideNavFrame,
  useSideNavCollapse,
} from "../side-nav"

// Side navigation glyphs from the design system (currentColor).
import { ReactComponent as PerformanceIcon } from "assets/svg/side-nav/performance.svg"
import {
  UsersIcon,
} from "@heroicons/react/24/outline"

import { useAuthStore } from "stores"
import { isLocalOrDevOrStaging, isPadeDomain } from "utils/utils"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import PadeLogoWhite from "assets/logos/paidhr-logo-white.svg"

export const SideNavigationBar: React.FC<{
  isSidebarOpen: boolean
  toggleSidebar: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isSidebarOpen, toggleSidebar }) => {
  const { compact } = useSideNavCollapse()
  const { user } = useAuthStore()
  const { customizationSettings } = useCustomizationSettingsStore()
  const { newPerformanceRoutes } = useNetworkEnabledRoutes()

  const actions: ActionType[] = [
    {
      title: "Direct Reports",
      icon: <UsersIcon className="w-4" />,
      route: "direct-reports",
      patterns: [{ path: "/manager/direct-reports/*" }],
      enabled: true,
      badge: false,
    },
    ...newPerformanceRoutes,
  ]

  const handleToggleSidebar = () => {
    toggleSidebar(false)
    document.body.classList.remove("overflow-hidden")
  }

  return (
    <>
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black/30  cursor-pointer backdrop-blur-sm"
          onClick={handleToggleSidebar}
        />
      )}
      <SideNavFrame
        isSidebarOpen={isSidebarOpen}
        compact={compact}
        zIndexClass="z-30"
        backdrop
      >
        <div className="h-full w-full  px-3 flex flex-col justify-start gap-3 relative z-[2]">
          {/* Brand */}
          <BrandLogoBlock
            compact={compact}
            orgName={user?.userOrganisation}
            alt="PaidHR Logo"
            src={
              isPadeDomain()
                ? PadeLogoWhite
                : customizationSettings?.brand?.menuLogoUrl
            }
          />
          {/* Menu */}
          <div className="h-full w-full flex flex-col gap-9">
            {user?.userStatusId === 5 ? (
              ""
            ) : (
              <div className="h-full w-full flex flex-col">
                <div className="h-full w-full flex flex-col">
                  <div className="pb-20 w-full flex flex-col gap-0.5">
                    {actions.map((action, index) =>
                      !action.enabled ? (
                        ""
                      ) : (
                        <NavTab
                          action={action}
                          key={index}
                          index={index}
                          toggleSidebar={handleToggleSidebar}
                          compact={compact}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SideNavFrame>
    </>
  )
}

const useNetworkEnabledRoutes = () => {
  const user = useAuthStore((state) => state.user)
  const {
    isLoadingPerformanceSetupProgress,
    isPerformanceSetupComplete,
    isHOD,
    isLoadingHODCheck,
  } = useAuthStore((state) => ({
    isLoadingPerformanceSetupProgress: state.user?.isLoadingPerformanceTracker,
    isPerformanceSetupComplete: state.user?.isNewPerformanceSetupCompleted,
    isHOD: state.user?.isHOD,
    isLoadingHODCheck: state.user?.isLoadingHODCheck,
  }))

  const hodPrefix = isHOD ? "hod/" : ""

  const newPerformanceRoutes =
    isLoadingHODCheck ||
    isLoadingPerformanceSetupProgress ||
    !isPerformanceSetupComplete
      ? []
      : [
          {
            title: "Performance",
            icon: <PerformanceIcon className="w-4" />,
            enabled: user?.isPerformanceEnabled === true,
            // isLocalOrDevOrStaging() || user?.userOrganisationId === 22,
            badge: false as ActionType["badge"],
            subActions: [
              {
                title: "Overview",
                route: `performance/${hodPrefix}overview`,
                patterns: [
                  {
                    path: `/manager/performance/${hodPrefix}overview/*`,
                  },
                ],
                enabled: true,
              },
              {
                title: "OKRs / KPIs",
                route: `performance/${hodPrefix}okr`,
                patterns: [
                  {
                    path: `/manager/performance/${hodPrefix}okr/*`,
                  },
                  // {
                  //   path: `/manager/performance/${hodPrefix}okr/*`,
                  // },
                  {
                    path: `/manager/performance/${hodPrefix}okr/individual/*`,
                  },
                ],

                enabled: true,
              },
              {
                title: "Reviews",
                route: `performance/${hodPrefix}review/review-period`,
                patterns: [
                  {
                    path: `/manager/performance/${hodPrefix}review/*`,
                  },
                  {
                    path: `/manager/performance/review/*`,
                  },
                ],
                enabled: true,
              },
            ],
          },
        ]
  return { newPerformanceRoutes }
}
